/**
 * Survey Responses Component
 *
 * Simple list view of survey responses
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MessageSquare,
  FileText,
  Eye,
  AlertTriangle,
  CheckCircle,
  Download,
  Mic,
  Keyboard,
  Clock,
  Zap,
} from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import { getSurveyResponses } from '../../utils/surveyApi';
import { UI_TEXT } from '../../config/ui-text';
import {
  STATE_FILTERS,
  countResponsesByState,
  filterResponsesByState,
  type StateFilter,
} from './responseFilter';
import { formatDuration } from '../../lib/utils';

interface QualitySummary {
  total_responses: number;
  flagged_responses: number;
  flag_types: Record<string, number>;
  quality_score: number;
}

interface Analytics {
  input_mode_stats: {
    mic_responses: number;
    text_responses: number;
    unknown_responses: number;
  };
  primary_input_mode: 'mic' | 'text' | null;
  mixed_mode: boolean;
}

interface ResponseSummary {
  session_id: number;
  state?: string;
  response_number?: number | null;
  completed_at: string | null;
  started_at?: string | null;
  last_activity_at?: string | null;
  responses_count: number;
  quality_summary?: QualitySummary;
  analytics?: Analytics;
  has_media_responses?: boolean;
  time_spent_seconds?: number | null;
  active_time_seconds?: number | null;
  external_tracking_id?: string | null;
  customer_id?: string | number | null;
  /** Per-respondent progress through each rapid-fire block they entered. Empty for everyone else. */
  rapid_fire?: RapidFireProgress[];
}

interface RapidFireProgress {
  block_id: string;
  label: string;
  answered: number;
  reached: number;
  pool_size: number;
}

interface SurveyResponsesData {
  survey_id: number;
  total_responses: number;
  total_attempts?: number;
  median_active_time_seconds?: number | null;
  responses: ResponseSummary[];
}

const SESSION_COMPLETED = 'completed';
const COMPLETED_LABEL = 'Completed';
const NO_MATCH_TITLE = 'No matching sessions';
const NO_MATCH_BODY = 'No sessions match this filter. Try “All”.';
const ACTIVE_TIME_SUFFIX = 'active';
const TOTAL_TIME_SUFFIX = 'total';
const TYPICAL_COMPLETION_PREFIX = 'Typical completion ≈';

/** Loose duration for the header summary: whole minutes above 1m, else seconds. */
const formatApproxDuration = (seconds: number): string =>
  seconds >= 60 ? `${Math.round(seconds / 60)}m` : `${Math.max(1, Math.round(seconds))}s`;

const renderStateBadge = (state?: string) => {
  if (!state || state === SESSION_COMPLETED) return null;
  const label =
    state === 'in_progress'
      ? 'In progress'
      : state === 'abandoned'
        ? 'Abandoned'
        : state === 'paused'
          ? 'Paused'
          : state;
  const cls = state === 'in_progress' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600';
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}
    >
      {label}
    </span>
  );
};

const SurveyResponses: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [responsesData, setResponsesData] = useState<SurveyResponsesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');

  const allResponses = responsesData?.responses ?? [];
  const stateCounts = countResponsesByState(allResponses);
  const visibleResponses = filterResponsesByState(allResponses, stateFilter);

  useEffect(() => {
    loadResponses();
  }, [id]);

  const loadResponses = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const result = await getSurveyResponses(parseInt(id));
      setResponsesData(result);
    } catch (error) {
      console.error('Failed to load survey responses:', error);
      setError(UI_TEXT.HUMAN_SURVEY.RESPONSES.ERRORS.FAILED_TO_LOAD_RESPONSES);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const renderQualityIndicator = (qualitySummary?: QualitySummary) => {
    if (!qualitySummary) return null;

    const { flagged_responses, quality_score } = qualitySummary;

    if (flagged_responses === 0) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
          <CheckCircle className="w-3 h-3 mr-1" />
          Good Quality
        </div>
      );
    }

    const colorClass =
      quality_score >= 80
        ? 'bg-green-100 text-green-800'
        : quality_score >= 60
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-red-100 text-red-800';

    return (
      <div
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colorClass}`}
      >
        {quality_score < 80 && <AlertTriangle className="w-3 h-3 mr-1" />}
        {flagged_responses} issue{flagged_responses !== 1 ? 's' : ''}
      </div>
    );
  };

  const renderInputModeIndicator = (analytics?: Analytics) => {
    if (!analytics) return null;

    const { primary_input_mode, mixed_mode, input_mode_stats } = analytics;

    // If all responses are unknown (legacy), don't show indicator
    if (input_mode_stats.mic_responses === 0 && input_mode_stats.text_responses === 0) {
      return null;
    }

    if (mixed_mode) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
          <Mic className="w-3 h-3 mr-1" />
          <Keyboard className="w-3 h-3" />
          <span className="ml-1">Mixed</span>
        </div>
      );
    }

    if (primary_input_mode === 'mic') {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
          <Mic className="w-3 h-3 mr-1" />
          Voice
        </div>
      );
    }

    if (primary_input_mode === 'text') {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 whitespace-nowrap">
          <Keyboard className="w-3 h-3 mr-1" />
          Text
        </div>
      );
    }

    return null;
  };

  const handleViewResponse = (sessionId: number) => {
    navigate(`/surveys/sessions/${sessionId}/responses`);
  };

  const handleExportCSV = async () => {
    if (!id) return;

    try {
      setIsExporting(true);

      // Make request to CSV export endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${id}/export/csv`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'text/csv',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Export failed');
      }

      // Get filename from Content-Disposition header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `survey_${id}_responses.csv`;

      if (contentDisposition) {
        // Try to extract quoted filename first: filename="something.csv"
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        } else {
          // Try unquoted filename: filename=something.csv
          filenameMatch = contentDisposition.match(/filename=([^;\s]+)/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      setError(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {UI_TEXT.HUMAN_SURVEY.RESPONSES.LOADING.LOADING_RESPONSES}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !responsesData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center py-8">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {UI_TEXT.HUMAN_SURVEY.RESPONSES.TITLES.UNABLE_TO_LOAD}
              </h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={() => navigate(`/surveys/${id}`)}
                icon={<ArrowLeft className="w-4 h-4" />}
              >
                {UI_TEXT.HUMAN_SURVEY.RESPONSES.BUTTONS.BACK_TO_SURVEY}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!responsesData) return null;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`/surveys/${id}`)}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              Back to Survey
            </Button>

            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                {UI_TEXT.HUMAN_SURVEY.RESPONSES.LABELS.COMPLETED_RESPONSES(
                  responsesData.total_responses,
                )}
                {typeof responsesData.total_attempts === 'number' &&
                  responsesData.total_attempts > responsesData.total_responses && (
                    <span className="text-gray-400">
                      {' '}
                      · {responsesData.total_attempts - responsesData.total_responses} in progress
                    </span>
                  )}
                {responsesData.median_active_time_seconds != null && (
                  <span className="text-gray-400">
                    {' '}
                    · {TYPICAL_COMPLETION_PREFIX}{' '}
                    {formatApproxDuration(responsesData.median_active_time_seconds)}
                  </span>
                )}
              </div>

              {responsesData.responses.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  icon={<Download className="w-4 h-4" />}
                >
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </Button>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-2">
                <MessageSquare className="w-6 h-6 text-blue-500 mr-2" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {UI_TEXT.HUMAN_SURVEY.RESPONSES.TITLES.SURVEY_RESPONSES}
                </h1>
              </div>
              <p className="text-gray-600">
                {UI_TEXT.HUMAN_SURVEY.RESPONSES.DESCRIPTIONS.VIEW_INDIVIDUAL}
              </p>

              {/* State filter — newest sessions are listed first regardless of state; these let the
                viewer narrow to completed / in-progress / abandoned. */}
              {responsesData.responses.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {STATE_FILTERS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setStateFilter(key)}
                      className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                        stateFilter === key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {label} ({stateCounts[key]})
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Responses List */}
            {visibleResponses.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {visibleResponses.map((response) => (
                  <div key={response.session_id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex flex-col gap-3 min-w-0 flex-1">
                        {/* Completed rows show their stable backend completion number; partials show a
                          state badge. The list is recency-sorted/filtered, so the list index is NOT
                          completion order — when response_number is absent (e.g. backend not yet
                          deployed) we show a neutral "Completed" label rather than a wrong number. */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {!response.state || response.state === SESSION_COMPLETED ? (
                            response.response_number != null ? (
                              <div className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full whitespace-nowrap">
                                {UI_TEXT.HUMAN_SURVEY.RESPONSES.LABELS.RESPONSE_NUMBER(
                                  response.response_number,
                                )}
                              </div>
                            ) : (
                              <div className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full whitespace-nowrap">
                                {COMPLETED_LABEL}
                              </div>
                            )
                          ) : (
                            renderStateBadge(response.state)
                          )}

                          {renderQualityIndicator(response.quality_summary)}

                          {renderInputModeIndicator(response.analytics)}
                        </div>

                        {/* Metadata with icons - always on second row */}
                        <div className="flex items-center gap-4 flex-wrap text-sm text-gray-500">
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            {response.completed_at ? (
                              <>
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span>{formatDate(response.completed_at)}</span>
                              </>
                            ) : (
                              <>
                                <Clock className="w-4 h-4 flex-shrink-0" />
                                <span>
                                  Last active{' '}
                                  {formatDate(response.last_activity_at || response.started_at)}
                                </span>
                              </>
                            )}
                          </div>

                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <MessageSquare className="w-4 h-4 flex-shrink-0" />
                            <span>
                              {UI_TEXT.HUMAN_SURVEY.RESPONSES.LABELS.ANSWERS(
                                response.responses_count,
                              )}
                            </span>
                          </div>

                          {/* How far this respondent got in each timed section. "of N" is the pool
                            they could have faced, so a low number reads as "the clock stopped
                            them", not "they refused to answer". */}
                          {(response.rapid_fire ?? []).map((rf) => (
                            <div
                              key={rf.block_id}
                              className="flex items-center gap-1 whitespace-nowrap"
                              title={`${rf.label}: answered ${rf.answered} of ${rf.pool_size} in the pool; ${rf.reached} were shown before time ran out`}
                            >
                              <Zap className="w-4 h-4 flex-shrink-0 text-blue-600" />
                              <span>
                                {rf.label}:{' '}
                                <span className="tabular-nums font-medium">{rf.answered}</span>
                                <span className="text-gray-400"> of {rf.pool_size}</span>
                              </span>
                            </div>
                          ))}

                          {/* Active time (engaged, excluding tab-away/idle) is the primary metric;
                            wall-clock duration is secondary. Sessions without active-time data
                            (null → older backend / legacy rows) keep the original display. */}
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            {response.active_time_seconds != null ? (
                              <span>
                                {formatDuration(response.active_time_seconds)} {ACTIVE_TIME_SUFFIX}
                                {response.time_spent_seconds != null && (
                                  <span className="text-gray-400">
                                    {' '}
                                    · {formatDuration(response.time_spent_seconds)}{' '}
                                    {TOTAL_TIME_SUFFIX}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span>{formatDuration(response.time_spent_seconds)}</span>
                            )}
                          </div>

                          {response.has_media_responses && (
                            <div className="flex items-center gap-1 whitespace-nowrap">
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <span>Contains media</span>
                            </div>
                          )}

                          {response.external_tracking_id != null &&
                            String(response.external_tracking_id).trim() !== '' && (
                              <div className="flex items-center gap-1 min-w-0 max-w-full">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap flex-shrink-0">
                                  IID
                                </span>
                                <span
                                  className="font-mono text-xs text-gray-600 truncate"
                                  title={String(response.external_tracking_id)}
                                >
                                  {String(response.external_tracking_id)}
                                </span>
                              </div>
                            )}

                          {response.customer_id != null &&
                            String(response.customer_id).trim() !== '' && (
                              <div className="flex items-center gap-1 min-w-0 max-w-full">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap flex-shrink-0">
                                  Customer ID
                                </span>
                                <span
                                  className="font-mono text-xs text-gray-600 truncate"
                                  title={String(response.customer_id)}
                                >
                                  {String(response.customer_id)}
                                </span>
                              </div>
                            )}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewResponse(response.session_id)}
                        icon={<Eye className="w-4 h-4" />}
                        className="flex-shrink-0"
                      >
                        {UI_TEXT.HUMAN_SURVEY.RESPONSES.BUTTONS.VIEW_DETAILS}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : allResponses.length > 0 ? (
              <div className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{NO_MATCH_TITLE}</h3>
                <p className="text-gray-600">{NO_MATCH_BODY}</p>
              </div>
            ) : (
              <div className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {UI_TEXT.HUMAN_SURVEY.RESPONSES.TITLES.NO_RESPONSES}
                </h3>
                <p className="text-gray-600">
                  {UI_TEXT.HUMAN_SURVEY.RESPONSES.DESCRIPTIONS.NO_RESPONSES_YET}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SurveyResponses;
